import { useCallback, useEffect, useRef, useState } from 'react';

import { Container, useTheme, useMediaQuery } from '@mui/material';

import { HeadingTag } from '@/types';
import {
	GoogleMapTemplate as GoogleMapTemplateType,
	Maybe,
	Asset,
	BffFacility,
	BffBrandType,
	GoogleMapTemplateNoResultsFoundMessage,
	Button,
	Header,
	MultiCardV2 as TMultiCardV2,
	ImageAsset as TImageAsset,
	GoogleMapTemplateSingleComingSoonMessage,
	Tabs,
} from '@/types/generated';
import { useFacilityContext } from '@/context';
import { useAppContext } from '@/context/AppContextProvider.ctx';
import { getBffNearByFacilitiesByLocation } from '@/services/getBffNearByFacilitiesByLocation';
import { getBffFacilitiesByAddress } from '@/services';
import { IUser, USA_CENTER_COORDINATES, USA_DEFAULT_DESKTOP_ZOOM, USA_DEFAULT_MOBILE_ZOOM } from '@/constants';
import { textToJumpLink } from '@/utils/textToJumpLink/textToJumpLink';
import { getUsersPosition } from '@/services/getPosition';
import { searchResultsEvent } from '@/utils';

import { getContainerStyles } from './GoogleMap.styles';
import { RequestStatus } from './GoogleMap.helpers';
import StandardGoogleMap from './StandardGoogleMap';
import ModernGoogleMap from './ModernGoogleMap';
import CumulativeGoogleMap from './CumulativeGoogleMap';

export interface IFacilitiesData {
	items: BffFacility[];
	reqStatus?: RequestStatus;
}

export interface IGoogleMap extends GoogleMapTemplateType {
	/**
	 * Optional field for the title of the Google Map
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	title?: Maybe<string>;
	/**
	 * Title heading that will set the H-tag to appear in the HTML without effecting the style of the element. normal will render a paragraph tag.
	 * @type {HeadingTag}
	 * @memberof IGoogleMap
	 * @default "h1"
	 */
	titleHtag?: Maybe<HeadingTag>;
	/**
	 * Optional field for the desktop zoom level
	 * Default: 10
	 *
	 * @type {number}
	 * @memberof IGoogleMap
	 */
	desktopZoom?: Maybe<number>;
	/**
	 * Optional field for the mobile zoom level
	 * Default: 9
	 *
	 * @type {number}
	 * @memberof IGoogleMap
	 */
	mobileZoom?: Maybe<number>;

	/**
	 * Optional field for default image
	 *
	 * @type {Asset}
	 * @memberof IGoogleMap
	 */
	defaultImage?: Maybe<Asset>;

	/**
	 * Optional field for selected image
	 *
	 * @type {Asset}
	 * @memberof IGoogleMap
	 */
	selectedImage?: Maybe<Asset>;
	/**
	 * Optional field for Google Map URL for business query
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	googleMapsBusinessQuery?: Maybe<string>;
	/**
	 * Optional field for customized holiday hours message text
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	holidayHoursMessageText?: Maybe<string>;
	/**
	 * Optional field Rich Text for no results found message
	 *
	 * @type {ContentTypeRichTextContent}
	 * @memberof IGoogleMap
	 */
	noResultsFoundMessage?: Maybe<GoogleMapTemplateNoResultsFoundMessage>;
	/**
	 * Optional field Rich Text for single coming soon location message
	 *
	 * @type {ContentTypeRichTextContent}
	 * @memberof IGoogleMap
	 */
	singleComingSoonMessage?: Maybe<GoogleMapTemplateSingleComingSoonMessage>;
	/**
	 * Optional field for no results found icon. Only for modern design
	 *
	 * @type {Maybe<ImageAsset>}
	 * @memberof IPatientReviews
	 */
	noResultsFoundImage?: Maybe<TImageAsset>;

	/**
	 * Optional field for full width
	 *
	 * @type {boolean}
	 * @memberof IGoogleMap
	 */
	fullWidth?: boolean;

	/**
	 * Optional field for scrolling the List Tab Horizontally
	 *
	 * @type {boolean}
	 * @memberof IGoogleMap
	 */
	listTabIsHorizontalScrolling?: boolean;

	/**
	 * Optional field for badge display
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	facilityBadgeLabel?: string;

	/**
	 * Optional field for facility details button
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	facilityButtonLabel?: string;
	/**
	 * Optional field for hiding the map and using only the location cards. Standard design only.
	 * @type {boolean}
	 * @memberof IGoogleMap
	 */
	isLocationCardsInHeader?: boolean;
	/**
	 * The Schedule button on the location cards.
	 *
	 * @type {Maybe<Button>}
	 * @memberof IGoogleMap
	 */
	scheduleButton?: Maybe<Button>;
	/**
	 * The Office Details button on the location cards.
	 *
	 * @type {Maybe<Button>}
	 * @memberof IGoogleMap
	 */
	officeDetailsButton?: Maybe<Button>;
	locationBarFindOfficeUrl?: Header['locationBarFindOfficeUrl'];
	/**
	 * The switch to use the modern design for the location search.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	useModernDesign?: Maybe<boolean>;

	/**
	 * The switch to use the cumulative design with state-grouped locations.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	useCumulativeDesign?: Maybe<boolean>;

	/**
	 * Optional field for no results content
	 *
	 * @type {MultiCardV2}
	 * @memberof IGoogleMap
	 */
	noResultsContent?: Maybe<TMultiCardV2>;
	/**
	 * A list of Services provided as Tabs > Accordion
	 *
	 * @type {Maybe<Tabs>}
	 * @memberof IGoogleMap
	 */
	availableServicesTabs?: Maybe<Tabs>;
	/**
	 * Set as office btn text if feature is true
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	setAsOfficeButtonText?: string;
	/**
	 * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IGoogleMap
	 */
	anchorId?: Maybe<string>;
	/**
	 * This will hide the location card buttons (schedule, details, etc). and disable the map pin click to ODP.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	hideLocationCardButtons?: Maybe<boolean>;
	/**
	 * This will hide only the Office Details button on location cards.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	hideOfficeDetailsButton?: Maybe<boolean>;
	/**
	 * This will disable the map marker click functionality, preventing navigation to ODP.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	disableMarkerClick?: Maybe<boolean>;
	/**
	 * This hides the locations list on mobile when the user scrolls away from the map on the page. This behavior is intended for the Lovet brand only.
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	hideLocationsListOnMobile?: Maybe<boolean>;
}

const continentalUSZoomAndCenter = {
	mapCenter: USA_CENTER_COORDINATES,
	desktopZoom: USA_DEFAULT_DESKTOP_ZOOM,
	mobileZoom: USA_DEFAULT_MOBILE_ZOOM,
};

/**
 * Google Map component that supports three different display modes:
 *
 * 1. Standard Map (default):
 *    - Traditional layout with map and location cards
 *    - Supports location search and nearby facility display
 *
 * 2. Modern Map (useModernDesign=true):
 *    - Enhanced UI with modern styling
 *    - Includes available services tabs
 *    - Improved search experience
 *    - Supports custom no-results content
 *
 * 3. Cumulative Map (useCumulativeDesign=true):
 *    - Groups locations by state
 *    - Shows all facilities at once
 *    - Optimized for viewing large numbers of locations
 *    - Takes precedence over useModernDesign if both are true
 */

export default function GoogleMap(props: Readonly<IGoogleMap>) {
	const {
		desktopZoom = 10,
		mobileZoom = 9,
		title,
		titleHtag = 'h1',
		defaultImage,
		selectedImage,
		googleMapsBusinessQuery,
		holidayHoursMessageText,
		noResultsFoundMessage,
		singleComingSoonMessage,
		noResultsFoundImage,
		fullWidth = true,
		listTabIsHorizontalScrolling = false,
		facilityBadgeLabel,
		facilityButtonLabel,
		isLocationCardsInHeader = false,
		scheduleButton,
		officeDetailsButton,
		locationBarFindOfficeUrl,
		useModernDesign = false,
		useCumulativeDesign = false,
		noResultsContent,
		availableServicesTabs,
		setAsOfficeButtonText,
		anchorId,
		hideLocationCardButtons = false,
		hideOfficeDetailsButton = false,
		disableMarkerClick = false,
		hideLocationsListOnMobile = false,
	} = props;
	const [searchValue, setSearchValue] = useState('');
	const facilityContext = useFacilityContext(true);
	const { facilityCode, facilityBrand, officeInfo, nearbyFacilities, isWaitingForNearbyFacilities, locationState } =
		facilityContext;
	const [facilitiesData, setFacilitiesData] = useState<IFacilitiesData>({
		items: nearbyFacilities || [],
		reqStatus: RequestStatus.NotAsked,
	});
	const hasManualUpdateRef = useRef(false);
	// `fetchData` re-runs whenever its deps change — including the user's coordinates, which
	// can settle after the first fetch — so the same query can be fetched more than once.
	// Tracking the last reported term keeps the analytics contract of one `search_results`
	// push per search. (StrictMode's dev-only double-invoke is covered by this too.)
	const lastReportedSearchTermRef = useRef<string | null>(null);

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const { environment, config, appName, appVersion } = useAppContext();
	const { enforceContinentalUsZoom = false } = config?.featureFlags || {};
	const searchParams = globalThis?.location?.search ? new URLSearchParams(globalThis?.location?.search) : null;
	const searchQueryValueFromUrl = searchParams?.get('query') ?? '';

	const updateDataFromLocationSearch = (facilitiesByAddress: BffFacility[]) => {
		hasManualUpdateRef.current = true;
		setFacilitiesData((previousState) => ({
			...previousState,
			items: facilitiesByAddress,
		}));
	};

	/**
	 * Determines the fetching strategy for facilities:
	 * - 'all': Fetches all facilities at once, used when:
	 *    1. Continental US zoom is enforced (for wider geographic coverage)
	 *    2. Cumulative design is enabled (needs all locations for state grouping)
	 * - 'one': Fetches facilities based on current location or search query
	 *    Used for standard and modern designs to show nearby/relevant locations
	 */
	const [fetchType, setFetchType] = useState(enforceContinentalUsZoom || useCumulativeDesign ? 'all' : 'one');

	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const googleMapId =
		environment === 'prod' ? config.services.prod.GOOGLE_MAP_ID : config.services.nonprod.GOOGLE_MAP_ID;
	const googleMapApiKey =
		environment === 'prod' ? config.services.prod.GOOGLE_MAP_API_KEY : config.services.nonprod.GOOGLE_MAP_API_KEY;

	const facilityBasePath = config.features.otherApps.facilityDetailsBasePath;
	const shouldUseGoogleAutocompleteSearch = config.featureFlags?.useGoogleMapAutocompleteSearch;
	const fetchAllLocationsOnEmptyResults = config.featureFlags?.fetchAllLocationsOnEmptyResults;

	const currentFacilityLatitude = officeInfo?.location?.latitude as number;
	const currentFacilityLongitude = officeInfo?.location?.longitude as number;

	const revertToCurrentFacility = async () => {
		const position = await getUsersPosition();
		void fetchData(position.coords.latitude, position.coords.longitude, '');
	};

	const handleRevertToCurrentFacility = () => {
		void revertToCurrentFacility();
	};

	const fetchAllFacilities = useCallback(
		async (user: IUser['coordinates']) => {
			setFacilitiesData((previousState) => ({
				...previousState,
				reqStatus: RequestStatus.Loading,
			}));
			// only get CDN coords
			const { latitude, longitude } = user || USA_CENTER_COORDINATES;

			const radius = 2800; // approx. radius to cover continental U.S.
			const maxItems = 1000;
			const facilities = await getBffNearByFacilitiesByLocation(
				facilityBrand.toString() as BffBrandType,
				latitude,
				longitude,
				url,
				appName,
				appVersion,
				{
					// pass params to service
					radiusMiles: radius,
					maxItems: maxItems,
					fetchAllIfNoResults: true,
				}
			);
			const facilityCollection = facilities?.facilityByLocation;

			setFacilitiesData({
				items: (facilityCollection as BffFacility[]) || [],
				reqStatus: RequestStatus.Success,
			});
			setSearchValue(searchQueryValueFromUrl);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[appName, appVersion, facilityBrand, searchQueryValueFromUrl, url]
	);

	const userFacilityLatitude = locationState?.user?.coordinates?.latitude || USA_CENTER_COORDINATES.latitude;
	const userFacilityLongitude = locationState?.user?.coordinates?.longitude || USA_CENTER_COORDINATES.longitude;

	const fetchData = useCallback(
		async (latitude: number, longitude: number, searchQueryValueFromUrl?: string) => {
			setFacilitiesData((previousState) => ({
				...previousState,
				reqStatus: RequestStatus.Loading,
			}));

			try {
				if ((!latitude || !longitude) && !searchQueryValueFromUrl) {
					throw new Error('no location info or search query');
				}
				// do not use search string if contains near
				const searchString = searchQueryValueFromUrl?.includes('near') ? '' : searchQueryValueFromUrl ?? '';
				let facilities;

				if (searchString) {
					const facilitiesListByAddress = await getBffFacilitiesByAddress(
						facilityBrand.toString() as BffBrandType,
						searchString,
						url,
						fetchAllLocationsOnEmptyResults,
						appName,
						appVersion
					);

					const addressResults = facilitiesListByAddress?.facilitiesByAddress;

					facilities = addressResults;

					if (lastReportedSearchTermRef.current !== searchString) {
						lastReportedSearchTermRef.current = searchString;
						// Reports what the user sees. With `fetchAllLocationsOnEmptyResults` on, a
						// zero-match search is backfilled with every facility and the map renders
						// them, so the count is that list — brands with the flag off report 0.
						searchResultsEvent({
							searchTerm: searchString,
							searchResultCount: addressResults?.length ?? 0,
						});
					}
				} else {
					const facilitiesListByLocation = await getBffNearByFacilitiesByLocation(
						facilityBrand.toString() as BffBrandType,
						latitude,
						longitude,
						url,
						appName,
						appVersion
					);
					facilities = facilitiesListByLocation?.facilityByLocation;
				}

				setFacilitiesData({
					items: (facilities as BffFacility[]) || [],
					reqStatus: RequestStatus.Success,
				});
				setSearchValue(searchQueryValueFromUrl ?? '');
			} catch (e: unknown) {
				setFacilitiesData((previousState) => ({
					...previousState,
					reqStatus: RequestStatus.Failure,
					// error: e instanceof Error ? e.message : 'An unknown error occurred',
				}));
				setFetchType('all');
				void fetchAllFacilities({
					latitude: userFacilityLatitude,
					longitude: userFacilityLongitude,
				});
				console.info('Fetching all facilities as a fallback due to error:', e);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[appName, appVersion, facilityBrand, url, userFacilityLatitude, userFacilityLongitude]
	);

	useEffect(() => {
		// wait for nearbyFacilities to load before t fetching data
		if (isWaitingForNearbyFacilities) {
			console.log('Waiting for nearby facilities to load...');
			return;
		}

		if (nearbyFacilities === undefined) {
			return;
		}

		// Skip sync if data was manually updated via updateDataFromLocationSearch
		if (hasManualUpdateRef.current) {
			return;
		}

		if (nearbyFacilities?.length && !searchQueryValueFromUrl) {
			// if nearbyFacilities is not empty and searchQueryValueFromUrl is empty, update state with nearbyFacilities
			setFacilitiesData({
				items: nearbyFacilities,
				reqStatus: RequestStatus.Success,
			});
		} else {
			void fetchData(currentFacilityLatitude, currentFacilityLongitude, searchQueryValueFromUrl);
		}
	}, [
		nearbyFacilities,
		fetchType,
		searchQueryValueFromUrl,
		currentFacilityLatitude,
		currentFacilityLongitude,
		isWaitingForNearbyFacilities,
		locationState,
		fetchData,
	]);

	const googleMapsSectionProps = {
		nearByFacilities: facilitiesData.items,
		facilityBasePath,
		isSmallScreen,
		currentFacilityLatitude,
		currentFacilityLongitude,
		facilityBrand,
		themeId: config.themeId,
		fullWidth,
		mobileZoom,
		desktopZoom,
		mapCenter: {
			latitude: facilitiesData.items[0]?.location?.latitude ?? currentFacilityLatitude,
			longitude: facilitiesData.items[0]?.location?.longitude ?? currentFacilityLongitude,
		},
		googleMapId,
		googleMapApiKey,
		...(fetchType === 'all' || !currentFacilityLatitude || !currentFacilityLongitude
			? continentalUSZoomAndCenter
			: {}),
	};

	const showSetOfficeButtonInGoogleMapsCards = config?.featureFlags?.showSetOfficeButtonInGoogleMapsCards;

	const nearByLocationsCardsProps = {
		defaultImage,
		selectedImage,
		googleMapsBusinessQuery,
		facilityBasePath,
		isSmallScreen,
		nearByFacilities: facilitiesData.items,
		currentFacilityId: facilityCode?.toString() ?? '',
		holidayHoursMessageText,
		noResultsFoundMessage,
		facilityBadgeLabel,
		facilityButtonLabel,
		scheduleButton,
		officeDetailsButton,
		isLocationCardsInHeader,
		setAsOfficeButtonText,
		showSetOfficeButtonInGoogleMapsCards,
	};

	if (useCumulativeDesign) {
		return (
			<CumulativeGoogleMap
				title={title}
				titleHtag={titleHtag}
				facilitiesData={facilitiesData}
				googleMapsSectionProps={googleMapsSectionProps}
				isSmallScreen={isSmallScreen}
				facilityBasePath={facilityBasePath}
				anchorId={anchorId}
			/>
		);
	}

	return (
		<Container
			{...(anchorId && { id: textToJumpLink(anchorId) })}
			data-testid="google_maps_template_container"
			sx={getContainerStyles(theme, fullWidth)}
			maxWidth={false}
		>
			{useModernDesign ? (
				<ModernGoogleMap
					searchValue={searchValue}
					setSearchValue={setSearchValue}
					facilitiesData={facilitiesData}
					googleMapsSectionProps={googleMapsSectionProps}
					nearByLocationsCardsProps={{
						...nearByLocationsCardsProps,
						noResultsFoundImage,
						singleComingSoonMessage,
						availableServicesTabs,
					}}
					fullWidth={fullWidth}
					isSmallScreen={isSmallScreen}
					updateDataFromLocationSearch={updateDataFromLocationSearch}
					handleRevertToCurrentFacility={handleRevertToCurrentFacility}
					noResultsContent={noResultsContent}
					currentFacilityId={facilityCode?.toString() ?? ''}
					hideLocationsListOnMobile={hideLocationsListOnMobile ?? false}
				/>
			) : (
				<StandardGoogleMap
					searchValue={searchValue}
					setSearchValue={setSearchValue}
					searchTitle={title}
					searchTitleHtag={titleHtag}
					facilitiesData={facilitiesData}
					googleMapsSectionProps={googleMapsSectionProps}
					nearByLocationsCardsProps={nearByLocationsCardsProps}
					fullWidth={fullWidth}
					isSmallScreen={isSmallScreen}
					isLocationCardsInHeader={isLocationCardsInHeader}
					listTabIsHorizontalScrolling={listTabIsHorizontalScrolling}
					updateDataFromLocationSearch={updateDataFromLocationSearch}
					handleRevertToCurrentFacility={handleRevertToCurrentFacility}
					locationBarFindOfficeUrl={locationBarFindOfficeUrl}
					useGoogleSearch={shouldUseGoogleAutocompleteSearch}
					hideLocationCardButtons={hideLocationCardButtons}
					hideOfficeDetailsButton={hideOfficeDetailsButton}
					disableMarkerClick={disableMarkerClick}
				/>
			)}
		</Container>
	);
}
